// Contracts for table tournaments («Турниры за столами», sub-project 8): knockout by stages at game tables,
// buy-in in tokens → prize pool (−feeBps), the best human of each table advances; bots only fill seats.
//   GET  /api/tourneys?status=          → { items: TourneySummary[] }
//   GET  /api/tourneys/:id              → TourneyDetails
//   POST /api/tourneys/:id/register     → TourneySummary
//   POST /api/tourneys/:id/unregister   → TourneySummary
//   POST /api/admin/tourneys            (zTourneyCreate) → TourneySummary
//   POST /api/admin/tourneys/:id/cancel → TourneySummary (all buy-ins refunded)
//   socket: tourney:watch / tourney:unwatch, pushes tourney:update / tourney:seat / tourney:result
import { z } from 'zod';
import type { GameId } from './common';

/** Games that have a tournament table adapter on the server. */
export const TOURNEY_GAMES = ['mahjong-table', 'doudizhu'] as const satisfies readonly GameId[];
export type TourneyGame = (typeof TOURNEY_GAMES)[number];
export const TOURNEY_BUYIN_PRESETS = [88, 288, 888] as const;
export const TOURNEY_SIZES = [16, 64] as const;
export type TourneyStatus = 'registering' | 'running' | 'finished' | 'cancelled';
export type TourneyEntryStatus = 'registered' | 'playing' | 'out' | 'winner' | 'refunded';

export const zTourneyCreate = z
  .object({
    game: z.enum(TOURNEY_GAMES),
    title: z.string().trim().min(3).max(60),
    startsAt: z.string().datetime(),
    buyIn: z.number().int().min(10).max(100_000),
    maxPlayers: z.union([z.literal(16), z.literal(64)]),
    minPlayers: z.number().int().min(2).max(64),
    handsPerStage: z.number().int().min(1).max(16),
    comment: z.string().trim().min(3).max(300),
  })
  .strict();
export type TourneyCreateInput = z.infer<typeof zTourneyCreate>;

export interface TourneySummary {
  id: string;
  game: TourneyGame;
  title: string;
  status: TourneyStatus;
  buyIn: number;
  feeBps: number;
  prizePool: number;
  players: number;
  maxPlayers: number;
  minPlayers: number;
  handsPerStage: number;
  startsAt: string;
  stage: number;
  seedHash: string;
  me: { status: TourneyEntryStatus; place: number | null; prize: number | null; tableId: string | null } | null;
}
export interface TourneySeatRow {
  seat: number;
  /** null = bot */
  player: { publicId: string; nickname: string; avatar: string | null } | null;
  points: number;
  wins: number;
  dealIns: number;
  hands: number;
  advanced: boolean;
}
export interface TourneyTableRow {
  stage: number;
  tableNo: number;
  tableId: string;
  status: 'playing' | 'done' | 'cancelled';
  handsDone: number;
  /** public numbers (`GameRound.roundNo`) of the table's played hands, in order — each is checked on the fair page */
  rounds: number[];
  seats: TourneySeatRow[];
}
export interface TourneyPlaceRow {
  place: number;
  publicId: string;
  nickname: string;
  avatar: string | null;
  stageOut: number | null;
  prize: number | null;
  me: boolean;
}
export interface TourneyDetails {
  tourney: TourneySummary;
  /** revealed after the start */
  seed: string | null;
  tables: TourneyTableRow[];
  places: TourneyPlaceRow[];
  /** by place (1st, 2nd, …) */
  prizes: number[];
}

export interface TourneyServerToClient {
  /** re-read the details */
  'tourney:update': (id: string) => void;
  /** «your table is ready» */
  'tourney:seat': (e: { tourneyId: string; tableId: string; game: TourneyGame; stage: number }) => void;
  /** the final place; `buyIn` — to tell a prize bigger than the buy-in (celebrated) from a smaller one (quiet) */
  'tourney:result': (e: { id: string; title: string; place: number; prize: number; buyIn: number }) => void;
}
export interface TourneyClientToServer {
  'tourney:watch': (id: string) => void;
  'tourney:unwatch': (id: string) => void;
}
